import React, { useEffect, useMemo, useRef, useState } from "react";
import { Database, Download, FileDown, Search, Star } from "lucide-react";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import reviewData from "../reviews/Azumaya Hai Ba Trung 1-google-maps-reviews_2026-09-07T08-40-07-939Z.json";

const businessName = [
    {
        id:"0x3135ab6aa3716a1f:0xa1774b7b61c4308" ,
        name: "Azumaya Dao Tan"
    },
    {
		id:"0x3135ab8d06272085:0xa2c488173a2bd380" ,
        name: "Azum chuywaya Hai Ba Trung 1"
    },
    {
        id:"0x3135ab695d39d9d3:0x3f7e6bcdc940aee" ,
        name: "Azumaya Kim Ma 2"
    },
    {
        id:"0x3135ab6c8e38f813:0x8b329251df596aaf" ,
        name: "Azumaya Kim Ma 3"
    },
    {
        id:"0x3135ab149718acdb:0xb77ce3d86ae19183" ,
        name: "Azumaya Linh Lang"
    },
    {
        id:"0x314a7128d14990fb:0xc186e81a79dc705a" ,
        name: "Azumaya Hai Phong"
    },
    {
        id:"0x3142181558c6ded1:0xd9b07e55bf351cab" ,
        name: "Azumaya Da Nang"
    },
    {
        id:"0x31752f48e8ac8da7:0xdf1add902008fcb9" ,
        name: "Azumaya Thai Van Lung 1"
    },
    {
        id:"0x31752f48c0091673:0xee667a931162b3a" ,
        name: "Azumaya Thai Van Lung 2"
    },
    {
        id:"0x31752f48d87c909f:0x3d392f32362ddb13" ,
        name: "Azumaya Le Thanh Ton"
    },
    {
        id:"0x3109513c1345804d:0xeeaf7f7f10b9ffc1" ,
        name: "Azumaya Phnom Penh"
    },
   
]
export default function GoogleMapsReview() {
	const [businessId, setBusinessId] = useState("");
	// const reviews = reviewData.reviews;
	const [selectedYear, setSelectedYear] = useState("all");
    const [reviews, setReviews] = useState([]);
	const [error, setError] = useState("");
	const [isLoading, setIsLoading] = useState(false);
	const [elapsedSeconds, setElapsedSeconds] = useState(0);
	const [isExporting, setIsExporting] = useState(false);
	const reportRef = useRef(null);

	useEffect(() => {
		if (!isLoading) return undefined;

		const timer = setInterval(() => {
			setElapsedSeconds((seconds) => seconds + 1);
		}, 1000);

		return () => clearInterval(timer);
	}, [isLoading]);

	const getReviews = async (event) => {
		event.preventDefault();
		const trimmedBusinessId = businessId.trim();

		if (!trimmedBusinessId) {
			setError("Please enter a Google Maps Business ID.");
			return;
		}

		setError("");
		setElapsedSeconds(0);
		setSelectedYear("all");
		setIsLoading(true);

		try {
			const response = await fetch(`/api/market/google-maps-reviews/${encodeURIComponent(trimmedBusinessId)}`);
			const responseText = await response.text();
			let data;

			try {
				data = JSON.parse(responseText);
			} catch (parseError) {
				throw new Error(`The server returned a non-JSON response (${response.status}): ${responseText}`);
			}

			if (!response.ok) {
				throw new Error(data.error || "Unable to retrieve Google Maps reviews.");
			}

			setReviews(data.reviews || []);
		} catch (requestError) {
			setReviews([]);
			setError(requestError.message);
		} finally {
			setIsLoading(false);
		}
	};

	const loadLocalReviews = () => {
		setError("");
		setElapsedSeconds(0);
		setSelectedYear("all");
		setBusinessId(reviewData.businessId || "");
		setReviews(reviewData.reviews || []);
	};

	const reviewsByYear = useMemo(() => {
		return reviews.reduce((counts, review) => {
			const year = review.iso_date?.slice(0, 4) || "Unknown";
			counts[year] = (counts[year] || 0) + 1;
			return counts;
		}, {});
	}, [reviews]);

	const years = useMemo(() => Object.keys(reviewsByYear).sort((left, right) => right.localeCompare(left)), [reviewsByYear]);

	const displayedReviews = useMemo(() => {
		return reviews
			.filter((review) => selectedYear === "all" || review.iso_date?.startsWith(selectedYear))
            .filter(review => review.review_rate > 0)
			.slice()
			.sort((left, right) => Number(right.iso_date_timestamp || 0) - Number(left.iso_date_timestamp || 0));
	}, [reviews, selectedYear]);

	const exportPdf = async () => {
		if (!reportRef.current || displayedReviews.length === 0) return;

		setIsExporting(true);
		try {
			const canvas = await html2canvas(reportRef.current, {
				scale: 1,
				useCORS: true,
				backgroundColor: "#ffffff"
			});
			const pdf = new jsPDF("p", "mm", "a4");
			const margin = 10;
			const pageWidth = pdf.internal.pageSize.getWidth();
			const pageHeight = pdf.internal.pageSize.getHeight();
			const contentWidth = pageWidth - margin * 2;
			const imageHeight = (canvas.height * contentWidth) / canvas.width;
			const image = canvas.toDataURL("image/png");
			let imageTop = margin;
			let remainingHeight = imageHeight;

			pdf.addImage(image, "PNG", margin, imageTop, contentWidth, imageHeight);
			remainingHeight -= pageHeight - margin * 2;

			while (remainingHeight > 0) {
				imageTop -= pageHeight - margin * 2;
				pdf.addPage();
				pdf.addImage(image, "PNG", margin, imageTop, contentWidth, imageHeight);
				remainingHeight -= pageHeight - margin * 2;
			}

			const year = selectedYear === "all" ? "all-years" : selectedYear;
			pdf.save(`google-maps-reviews-${year}.pdf`);
		} finally {
			setIsExporting(false);
		}
	};

	const exportCsv = () => {
		if (displayedReviews.length === 0) return;

		const escapeCsvValue = (value) => `"${String(value ?? "").replace(/"/g, '""')}"`;
		const business = businessName.find((item) => item.id === businessId)?.name || "";
		const summaryRows = [
			["Google Maps Reviews Summary"],
			["Business", business],
			["Business ID", businessId],
			["Total reviews loaded", reviews.length],
			["Reviews exported", displayedReviews.length],
			[],
			["Year", "Total reviews"]
		];
		const yearRows = years.map((year) => [year, reviewsByYear[year]]);
		const headers = ["Reviewer", "Rating", "Review", "Date", "Google Maps review link"];
		const rows = displayedReviews.map((review) => [
			review.user_name,
			review.review_rate,
			review.review_text,
			review.iso_date_of_last_edit || review.iso_date,
			review.review_link
		]);
		const csvRows = [...summaryRows, ...yearRows, [], headers, ...rows];
		const csv = csvRows.map((row) => row.map(escapeCsvValue).join(",")).join("\r\n");
		const blob = new Blob(["\uFEFF", csv], { type: "text/csv;charset=utf-8" });
		const url = URL.createObjectURL(blob);
		const link = document.createElement("a");
		const year = selectedYear === "all" ? "all-years" : selectedYear;

		link.href = url;
		link.download = `google-maps-reviews-${year}.csv`;
		link.click();
		URL.revokeObjectURL(url);
	};

	return (
		<main className="container py-4" style={{marginTop: 50}}>
			<div ref={reportRef}>
			<div className="mb-4">
				<h2 className="mb-1">Google Maps Reviews</h2>
				<p className="text-muted mb-0">{displayedReviews.length} of {displayedReviews.length} reviews, newest first.</p>
			</div>
            <h3>{businessName.find(business => business.id === businessId)?.name}</h3>
			<div className="mb-4" aria-label="Filter reviews by year">
				<button
					className={`btn btn-sm me-2 mb-2 ${selectedYear === "all" ? "btn-primary" : "btn-outline-secondary"}`}
					type="button"
					onClick={() => setSelectedYear("all")}
				>
					All ({displayedReviews.length})
				</button>
				{years.map((year) => (
					<button
						key={year}
						className={`btn btn-sm me-2 mb-2 ${selectedYear === year ? "btn-primary" : "btn-outline-secondary"}`}
						type="button"
						onClick={() => setSelectedYear(year)}
					>
						{year} ({reviewsByYear[year]})
					</button>
				))}
			</div>

			<form className="row g-2 align-items-end mb-4" onSubmit={getReviews}>
			{/* <form className="row g-2 align-items-end mb-4" onSubmit={(event) => event.preventDefault()}> */}
				<div className="col-md-8 col-lg-6">
					<label className="form-label" htmlFor="google-maps-business-id">Business ID</label>
					<input
						id="google-maps-business-id"
						className="form-control"
						value={businessId}
						onChange={(event) => setBusinessId(event.target.value)}
						placeholder="e.g. 0x3135ab..."
					/>
				</div>
				<div className="col-auto">
					<button className="btn btn-primary d-inline-flex align-items-center" type="submit" disabled={isLoading}>
						<Search size={18} className="me-2" />
						{isLoading ? "Retrieving..." : "Get from API"}
					</button>
				</div>
				<div className="col-auto">
					<button className="btn btn-outline-primary d-inline-flex align-items-center" type="button" onClick={loadLocalReviews} disabled={isLoading}>
						<Database size={18} className="me-2" />
						Get local data
					</button>
				</div>
				<div className="col-auto">
					<button className="btn btn-outline-secondary d-inline-flex align-items-center" type="button" onClick={exportPdf} disabled={isLoading || isExporting || displayedReviews.length === 0}>
						<Download size={18} className="me-2" />
						{isExporting ? "Creating PDF..." : "Export PDF"}
					</button>
				</div>
				<div className="col-auto">
					<button className="btn btn-outline-success d-inline-flex align-items-center" type="button" onClick={exportCsv} disabled={isLoading || displayedReviews.length === 0}>
						<FileDown size={18} className="me-2" />
						Export CSV
					</button>
				</div>
			</form>

			{isLoading && (
				<div className="mb-4" aria-live="polite">
					<div className="d-flex justify-content-between mb-1">
						<span>Retrieving all available reviews...</span>
						<span>{elapsedSeconds}s</span>
					</div>
					<div className="progress" style={{ height: 8 }}>
						<div className="progress-bar progress-bar-striped progress-bar-animated w-100" role="progressbar" aria-label="Retrieving reviews" />
					</div>
				</div>
			)}

			{error && <div className="alert alert-danger" role="alert">{error}</div>}

			{!isLoading && reviews.length > 0 && (
			// {displayedReviews.length > 0 && (
				<div className="table-responsive border rounded">
					<table className="table table-hover mb-0">
						<thead className="table-light">
							<tr>
								<th style = {{fontSize: "1.4rem"}} scope="col">Reviewer</th>
								<th style = {{fontSize: "1.4rem"}} scope="col">Rating</th>
								<th style = {{fontSize: "1.4rem"}} scope="col">Review</th>
								<th style = {{fontSize: "1.4rem"}} scope="col">Date</th>
							</tr>
						</thead>
						<tbody>
							{displayedReviews.map((review) => (
								<tr key={review.review_id}>
									<td>{review.user_name}</td>
									<td className="text-nowrap"><Star size={16} className="text-warning me-1" fill="currentColor" />{review.review_rate}</td>
									<td style={{ minWidth: 280, whiteSpace: "pre-wrap", textAlign: "left" }}>{review.review_text}</td>
									<td className="text-nowrap">{review.iso_date_of_last_edit}</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			)}

			{!isLoading && !error && reviews.length === 0 && (
				<p className="text-muted">Enter a Business ID to load reviews.</p>
			)}
			</div>
		</main>
	);
}
